import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, FormHelperText, Link, Stack, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { matchIsValidTel, MuiTelInput } from "mui-tel-input";
import firebase from "../../auth/firebase";
import { claimOrdinaryEmailSignIn, completeSignInWithEmailLink, signIn, verificationCode } from "../../auth/utils";
import { startRegisteredMobileLogin } from "../../auth/mobileLogin";
import { hasFirebaseAction } from "../../auth/consoleSession";
import { useAuthContext } from "../../context/AuthContext";
import { useTranslation } from "../../context/Internationalization";
import { LOGIN_ROUTE, REGISTRATION_ROUTE } from "../../routes";
import { readRegistrationProgress } from "../Registration/registrationProgress";
import { SessionAccessDenied, SessionError } from "../../Components/SessionBoundary";
import FullPageLoader from "../../Components/FullPageLoader";
import AuthLayout from "./AuthLayout";
import AuthTextField from './AuthTextField';
import useEmailCodeSignIn from './useEmailCodeSignIn';
import EmailCodeInput from './EmailCodeInput';
import appStoreBadge from "../../assets/images/store-badges/app-store.svg";
import googlePlayBadge from "../../assets/images/store-badges/google-play.svg";
import { colors } from '../../theme/colors';

const APP_STORE_DOWNLOAD_LINK = "https://apps.apple.com/gr/app/playerdex-academy/id1584881110";
const GOOGLE_PLAY_DOWNLOAD_LINK = "https://play.google.com/store/apps/details?id=app.playerdex.onetouchfootball";
const emailCodeFieldErrors = new Set(['email_code_invalid', 'email_code_expired', 'email_code_attempts_exhausted']);

export const Login = () => {
  const auth = useAuthContext();
  const { t, lang } = useTranslation();
  const [method, setMethod] = useState("email");
  const emailSignIn = useEmailCodeSignIn(auth, lang);
  const { email, setEmail, challenge, resendIn } = emailSignIn;
  const checkEmail = Boolean(challenge);
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [rememberPhone, setRememberPhone] = useState(() => Boolean(localStorage.getItem("rememberMe")));
  const [phoneBusy, setBusy] = useState(false);
  const busy = phoneBusy || emailSignIn.busy;
  const [error, setError] = useState("");
  const callbackStarted = useRef(false);
  const submitPending = useRef(false);
  const mounted = useRef(true);
  const [callbackBusy, setCallbackBusy] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("error")) {
      setError(params.get("error") === "login_session_expired" ? "login_session_expired" : "invalid_or_expired_login_link");
      window.history.replaceState(null, "", LOGIN_ROUTE);
    }
    if (params.get("mode") !== "signIn" || !params.get("oobCode") ||
      callbackStarted.current || !claimOrdinaryEmailSignIn()) return;
    callbackStarted.current = true;
    setCallbackBusy(true);
    completeSignInWithEmailLink().then(({ user, idToken }) => {
      window.history.replaceState(null, "", LOGIN_ROUTE);
      localStorage.setItem("lastLoginMethod", "email");
      return auth.refreshSession({ firebaseUser: user, token: idToken, force: true });
    }).catch((failure) => {
      if (!mounted.current) return;
      window.history.replaceState(null, "", LOGIN_ROUTE);
      setError(failure.message === "login_session_expired" ? "login_session_expired" :
        failure.code?.startsWith("auth/") || failure.message === "invalid_or_expired_login_link"
          ? "invalid_or_expired_login_link" : "registration_session_error");
    }).finally(() => {
      if (mounted.current) setCallbackBusy(false);
    });
  }, [auth.refreshSession]);

  const submitPhone = async (event) => {
    event.preventDefault();
    if (submitPending.current) return;
    if (!confirmation && !matchIsValidTel(mobile, { onlyCountries: ["GR", "CY"] })) {
      setError("error_invalid_phone_number"); return;
    }
    submitPending.current = true;
    setBusy(true);
    setError("");
    try {
      if (!confirmation) {
        if (rememberPhone) localStorage.setItem("rememberMe", "1");
        else localStorage.removeItem("rememberMe");
        const result = await startRegisteredMobileLogin(mobile.replace(/ /g, "").trim(), signIn);
        if (!result.isRegistered) {
          setMethod("email");
          emailSignIn.changeEmail();
          setError("mobile_not_registered_use_email");
          return;
        }
        setConfirmation(result.confirmationResult);
      } else {
        const token = await verificationCode(confirmation, code);
        const identity = firebase.auth().currentUser;
        const result = await auth.refreshSession({ firebaseUser: identity, token });
        if (!result.user.email) {
          const storage = localStorage.getItem("rememberMe") ? localStorage : sessionStorage;
          storage.setItem("lastLoginMethod", "sms");
        }
      }
    } catch (_) {
      if (mounted.current) setError(confirmation ? "registration_sms_code_failed" : "registration_sms_send_failed");
    } finally {
      submitPending.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  if (callbackBusy || auth.sessionStatus === "loading") return <FullPageLoader message={t("signing_in")} />;
  if (auth.sessionStatus === "error") return <SessionError />;
  if (auth.sessionStatus === "ready" && !hasFirebaseAction()) {
    if (auth.session?.kind === "restricted") return <SessionAccessDenied />;
    const pending = !auth.authState?.impersonate?._id && readRegistrationProgress(auth.firebaseUser?.uid);
    return <Navigate to={pending ? REGISTRATION_ROUTE : auth.session.destination} replace />;
  }

  const emailError = emailSignIn.error || (checkEmail && !emailSignIn.expiresIn ? 'email_code_expired' : '');
  const visibleError = error || (method === 'email' ? emailError : '');
  const emailFieldError = method === 'email' && !checkEmail && visibleError === 'invalid_email' ? visibleError : '';
  const emailCodeFieldError = method === 'email' && checkEmail && emailCodeFieldErrors.has(visibleError) ? visibleError : '';
  const phoneFieldError = method === 'phone' && !confirmation && visibleError === 'error_invalid_phone_number' ? visibleError : '';
  const smsCodeFieldError = method === 'phone' && confirmation && visibleError === 'registration_sms_code_failed' ? visibleError : '';
  const formError = emailFieldError || emailCodeFieldError || phoneFieldError || smsCodeFieldError ? '' : visibleError;

  return (
    <AuthLayout>
      <Stack spacing={3}>
        <Box>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t(method === "phone" ? "login_with_phone" : checkEmail ? "registration_check_email" : "registration_welcome")}
          </Typography>
          <Typography id="email-code-help" color="text.secondary" variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {method === 'email' && checkEmail ? <>{t('email_code_sent_to')}{' '}<strong>{email}</strong>.{' '}{t('email_code_expiry_help')}</> :
              t(method === "phone" ? "registration_phone_description" : "registration_email_description")}
          </Typography>
        </Box>
        {formError && <Alert id="login-error" severity="error">{t(formError)}</Alert>}
        <div id="recaptcha-container" />
        {method === "email" && (checkEmail ? <Stack component="form" onSubmit={(event) => { setError(''); emailSignIn.verify(event); }} spacing={3} noValidate>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <EmailCodeInput value={emailSignIn.code} onChange={(value) => {
                emailSignIn.setCode(value);
                if (emailCodeFieldErrors.has(emailSignIn.error)) emailSignIn.setError('');
              }} disabled={busy || !emailSignIn.expiresIn}
                label={t('email_code_label')} error={Boolean(emailCodeFieldError)}
                descriptionId={emailCodeFieldError ? 'email-code-error email-code-help' : 'email-code-help'} />
            </Box>
            {emailCodeFieldError && <FormHelperText id="email-code-error" error role="alert" sx={{ mx: 1.5, mt: 1 }}>
              {t(emailCodeFieldError)}
            </FormHelperText>}
          </Box>
          <LoadingButton variant="contained" type="submit" loading={busy} disabled={emailSignIn.code.length !== 6 || !emailSignIn.expiresIn}>
            {t('email_code_verify')}
          </LoadingButton>
          <Typography variant="body2" textAlign="center">{t('email_code_not_received')}{' '}
            {resendIn > 0 ? <Typography component="span" variant="inherit" color="text.secondary">
              {`${t('email_code_resend_in')} ${Math.floor(resendIn / 60)}:${String(resendIn % 60).padStart(2, '0')}`}
            </Typography> : <Link component="button" type="button" variant="inherit" underline="always" color={colors.Primary[800]}
              disabled={busy} onClick={(event) => { setError(''); emailSignIn.send(event); }}
              sx={{ verticalAlign: 'baseline', '&:disabled': { color: 'text.secondary', textDecoration: 'none', cursor: 'default' } }}>
              {t('email_code_resend')}
            </Link>}
          </Typography>
          <Button variant="outlined" type="button" disabled={busy} onClick={() => { setError(''); emailSignIn.changeEmail(); }}>{t("registration_change_email")}</Button>
        </Stack> : <Stack component="form" onSubmit={(event) => { setError(''); emailSignIn.send(event); }} spacing={2} noValidate>
          <AuthTextField autoFocus hideLabel label={t("email")} name="email" type="email" required
            autoComplete="email" value={email} disabled={busy} onChange={(event) => {
              setEmail(event.target.value);
              if (emailSignIn.error === 'invalid_email') emailSignIn.setError('');
            }} error={Boolean(emailFieldError)} helperText={emailFieldError ? t(emailFieldError) : ''} />
          <LoadingButton variant="contained" loading={busy} disabled={resendIn > 0} type="submit">{t("registration_continue")}</LoadingButton>
        </Stack>)}
        {method === "phone" && <Stack component="form" onSubmit={submitPhone} spacing={2} noValidate>
          {!confirmation ? <>
            <AuthTextField component={MuiTelInput} label={t("mobile")} defaultCountry="GR" onlyCountries={["GR", "CY"]}
              value={mobile} onChange={(value) => { setMobile(value); if (phoneFieldError) setError(''); }} disabled={busy}
              error={Boolean(phoneFieldError)} helperText={phoneFieldError ? t(phoneFieldError) : ''}
              inputProps={{ autoComplete: "tel", name: "mobile" }} />
            <FormControlLabel control={<Checkbox checked={rememberPhone} disabled={busy}
              onChange={(event) => setRememberPhone(event.target.checked)} />} label={t("remember_me")} />
          </> : <AuthTextField autoFocus label={t("insert_code")} value={code} disabled={busy}
            error={Boolean(smsCodeFieldError)} helperText={smsCodeFieldError ? t(smsCodeFieldError) : ''}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
              if (smsCodeFieldError) setError('');
            }}
            inputProps={{ inputMode: "numeric", autoComplete: "one-time-code", maxLength: 6 }} />}
          <LoadingButton variant="contained" type="submit" loading={busy} disabled={Boolean(confirmation && code.length !== 6)}>
            {t(confirmation ? "registration_verify_sms" : "registration_send_sms")}
          </LoadingButton>
        </Stack>}
        {!(method === 'email' && checkEmail) && <>
        <Button variant="outlined" type="button" disabled={busy}
          startIcon={method === "email" ? <PhoneOutlinedIcon /> : <EmailOutlinedIcon />} onClick={() => {
          setError("");
          emailSignIn.setError('');
          if (method === "phone") {
            setConfirmation(null);
            setCode("");
          }
          setMethod(method === "email" ? "phone" : "email");
        }}>{t(method === "email" ? "login_with_phone" : "login_with_email")}</Button>
        <Divider><Typography component="span" variant="body2" color="text.secondary">{t("registration_or")}</Typography></Divider>
        <Typography color="text.secondary" textAlign="center">{t("registration_mobile_app")}</Typography>
        <Stack direction="row" spacing={1} justifyContent="center">
          <Link href={APP_STORE_DOWNLOAD_LINK} target="_blank" rel="noopener noreferrer" aria-label={t("download_on_app_store")}>
            <Box component="img" src={appStoreBadge} alt="" sx={{ display: "block", width: 135, height: 40 }} />
          </Link>
          <Link href={GOOGLE_PLAY_DOWNLOAD_LINK} target="_blank" rel="noopener noreferrer" aria-label={t("get_it_on_google_play")}>
            <Box component="img" src={googlePlayBadge} alt="" sx={{ display: "block", width: 135, height: 40 }} />
          </Link>
        </Stack>
        </>}
      </Stack>
    </AuthLayout>
  );
};
export default Login;
